import React from 'react';
import { db } from '../../utils/firebase';
import { collection, getDocs, onSnapshot, query, orderBy } from 'firebase/firestore';
import { formatTime, calculateDuration } from '../../utils/dateUtils';
import { getImageUrl } from '../../utils/imageUtils';

const HomeView = ({ trails, onTrailSelect, onProfileClick, user }) => {
    const [selectedLocation, setSelectedLocation] = React.useState('all');
    const [selectedActivity, setSelectedActivity] = React.useState('all');

    // 1. Local state for Firestore-fetched trails
    const [fetchedTrails, setFetchedTrails] = React.useState([]);

    // 2. Fetch trails from Firestore on mount
    React.useEffect(() => {
        const trailsRef = collection(db, 'trails');
        
        // Fetch all trails without ordering first to avoid field issues
        // Then sort them in JavaScript to handle both old and new trails
        const unsubscribe = onSnapshot(trailsRef, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            // Sort trails: old trails (with 'order' field) first, then new trails by start_time
            const sortedData = data.sort((a, b) => {
                // If both have 'order' field, sort by order
                if (a.order !== undefined && b.order !== undefined) {
                    return a.order - b.order;
                }
                // If only one has 'order', prioritize it
                if (a.order !== undefined && b.order === undefined) {
                    return -1;
                }
                if (a.order === undefined && b.order !== undefined) {
                    return 1;
                }
                // If neither has 'order', sort by start_time
                const aTime = a.start_time?.toDate ? a.start_time.toDate() : new Date(a.start_time);
                const bTime = b.start_time?.toDate ? b.start_time.toDate() : new Date(b.start_time);
                return aTime - bTime;
            });
            
            console.log('Fetched and sorted trails from Firestore:', sortedData);
            setFetchedTrails(sortedData);
        }, (error) => {
            console.error('Error fetching trails in HomeView (real-time):', error);
        });

        return () => unsubscribe();
    }, []);

    // 3. Use fetchedTrails if available, else fall back to the prop
    const displayedTrails = fetchedTrails.length ? fetchedTrails : trails;

    // Predefined European cities grouped by country
    const europeanCities = {
        'Brazil': ['Rio de Janeiro', 'São Paulo', 'Florianópolis', 'Petrópolis'], 
        'Colombia': ['Bogota', 'Medellin', 'Cali', 'Barranquilla'],
        'France': ['Paris', 'Lyon', 'Marseille', 'Bordeaux', 'Nice'],
        'Italy': ['Rome', 'Milan', 'Florence', 'Venice', 'Naples'],
        'Portugal': ['Porto', 'Lisboa', 'Braga', 'Coimbra', 'Aveiro'],
        'Spain': ['Madrid', 'Barcelona', 'Granada', 'Valencia', 'Seville', 'Bilbao'],
        'United States':['Miami', 'Berkeley'],
    };

    // Filter by selected location and activity
    const filteredTrails = displayedTrails.filter(trail =>
        (selectedLocation === 'all' || trail.location === selectedLocation) &&
        (selectedActivity === 'all' || trail.activity === selectedActivity)
    );
    

    const handleTrailClick = (trail) => {
        if (user) {
            onTrailSelect(trail);
        } else {
            if (window?.showLoginModal) {
                window.showLoginModal(); // 👈 your login modal logic
            } else {
                alert("Please log in to continue");
            }
        }
    };
    

return ( 
  <div className="min-h-screen bg-white font-roboto">
    <main className="p-4">
      {/* Header Section */}
      <div className="max-w-3xl mx-auto text-center mb-6">
        <div className="mb-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            {user ? `Welcome back, ${user.displayName}!` : 'Welcome to Autsai!'}
          </h1>
        </div>
        <p className="text-gray-600 text-base md:text-lg mb-2">
           Pick a sport session. Go solo or get matched with the right sports partner.
        </p>
        <p className="text-purple-600 text-xs font-semibold tracking-wide uppercase">
          GO OFFLINE. GO AUTSAI.
        </p>

        {/* Visit Our Website Button */}
        <div className="mt-4 mb-2">
          <a
            href="https://www.autsai.com/" 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-2.5 text-sm font-semibold text-white rounded-full 
                       bg-gradient-to-r from-[#6B01D1] to-[#F2787A] shadow-md 
                       hover:opacity-90 transition duration-300"
          >
            Visit Our Website
          </a>
        </div>
      </div>

      {/* Filter Section */}
      <div className="max-w-3xl mx-auto mb-10 flex flex-row flex-wrap gap-4 px-4">
        {/* Activity Filter */}
        <div className="flex-1">
          <select
            id="activity-select"
            value={selectedActivity}
            onChange={(e) => setSelectedActivity(e.target.value)}
            className="w-full px-4 py-3 rounded-full border border-purple-300 
                       bg-white text-gray-700 text-sm font-medium 
                       shadow-sm transition-all duration-200 
                       appearance-none cursor-pointer"
          >
            <option value="all">All Activities</option>
            <option value="hiking">Hiking</option>
            <option value="running">Running</option>
            <option value="biking">Biking</option>
            <option value="swimming">Swimming</option>
          </select>
        </div>

        {/* Location Filter */}
        <div className="flex-1">
          <select
            id="location-select"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="w-full px-4 py-3 rounded-full border border-purple-300 
                       bg-white text-gray-700 text-sm font-medium 
                       shadow-sm transition-all duration-200 
                       appearance-none cursor-pointer"
          >
            <option value="all">All Locations</option>
            {Object.entries(europeanCities).map(([country, cities]) => (
              <optgroup key={country} label={country} className="text-sm">
                {cities.map(city => (
                  <option 
                    key={`${city}, ${country}`} 
                    value={`${city}, ${country}`}
                    className="text-gray-700"
                  >
                    {city}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>

                {/* Trails Grid */}
                <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredTrails.length > 0 ? (
                        filteredTrails.map(trail => (

                            (() => {
                                if (trail.start_time?.toDate) {
                                  const startDate = trail.start_time.toDate();
                                  const now = new Date();
                                  if (startDate < now) {
                                    const nextSaturday = new Date(now.setDate(now.getDate() + (6 - now.getDay() + 7) % 7));
                                    nextSaturday.setHours(10, 0, 0, 0);
                                    trail.start_time = nextSaturday;
                                  }
                                }
                              })(),                            
                            <div
                                key={trail.id}
                                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl 
                                         transform transition-all duration-300 hover:-translate-y-1 
                                         cursor-pointer border border-purple-50"
                                         onClick={() => {
                                            if (!user) {
                                              if (typeof window.setRequireAuth === 'function') {
                                                window.setRequireAuth(true);
                                              } else {
                                                console.warn("Login modal trigger not found.");
                                              }
                                            } else {
                                              onTrailSelect(trail);
                                            }
                                          }}                                                                           
                            >
                                <div className="relative">
                                    <img
                                        src={getImageUrl(trail.image)}
                                        alt={trail.name}
                                        className="w-full h-48 object-cover"
                                    />
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                                        <p className="text-white font-medium">{trail.location}</p>
                                    </div>
                                </div>
                                <div className="p-5">
                                <h2 className="text-xl font-semibold text-gray-800 mb-1">{trail.name}</h2>
                                    <p className="text-sm text-gray-500 mb-2">
                                        {new Date(trail.start_time?.toDate?.() ?? trail.start_time).toLocaleDateString('en-US', {
                                               weekday: 'long',
                                               month: 'long',
                                               day: 'numeric'
                                             })}
                                    </p>

                                    <p className="text-sm text-gray-600 flex items-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {formatTime(trail.start_time)}
                                        
                                        <span className="ml-2">
                                            (Est. {trail.hours} hrs)
                                        </span>
                                        {trail.activity === 'hiking' && (
                                            <i className="fas fa-hiking text-2xl text-gray-700 absolute bottom-5 right-5"></i>
                                        )}
                                        {trail.activity === 'running' && (
                                            <i className="fas fa-running text-2xl text-gray-700 absolute bottom-5 right-5"></i>
                                        )}
                                        {trail.activity === 'biking' && (
                                            <i className="fas fa-bicycle text-2xl text-gray-700 absolute bottom-5 right-5"></i>
                                        )}
                                        {trail.activity === 'swimming' && (
                                            <i className="fas fa-swimmer text-2xl text-gray-700 absolute bottom-5 right-5"></i>
                                        )}
                                    </p>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full text-center py-12 bg-gray-50 rounded-xl">
                            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                            <p className="text-xl text-gray-600 mb-2">
                                No trails currently available in {selectedLocation === 'all' ? 'any location' : selectedLocation}.
                            </p>
                            <p className="text-lg text-purple-600 font-medium">
                                We're working on adding more trails. Check back soon!
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default HomeView;